'use client';

import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSlotRemainingPlaces, isSlotBelowRequestedPartySize } from '@cfixe-monorepo/shared';
import type { BookableSlot } from '../../types';
import { addDays, parseLocalDate, toISODate } from '../../utils/date-helpers';
import { getSlotDate, getSlotHour, getSlotTimeLabel } from './slot-picker-utils';

interface SlotWeekPickerLabels {
  previousWeek: string;
  nextWeek: string;
  morning: string;
  afternoon: string;
  evening: string;
  noSlots: string;
  noSlotsForDay?: string;
  /** Annoncé aux lecteurs d'écran pendant le chargement (squelette de grille). */
  loading?: string;
  /* Compteur de places des séances de groupe — absents : compteur non rendu. */
  placesOne?: string;
  placesOther?: string;
  full?: string;
  insufficientForParty?: string;
}

interface SlotGroup {
  label: string;
  slots: BookableSlot[];
}

interface SlotWeekPickerProps {
  slots: BookableSlot[];
  selectedSlot: BookableSlot | undefined;
  selectedDate: string | undefined;
  /** Premier jour de la fenêtre de 7 jours affichée (les fenêtres partent d'aujourd'hui, cf. `slotWindowStart`). */
  weekStart: string;
  labels: SlotWeekPickerLabels;
  locale: string;
  loading?: boolean;
  variant?: 'tunnel' | 'manage';
  /*
   * Items du panier (ordre de la requête de créneaux) : sert à griser les créneaux séance dont
   * `remaining_places` < party_size demandé. Absent (reschedule phase 1) : compteur affiché,
   * jamais de grisage — le backend n'envoie alors que des créneaux réservables.
   */
  onSelectDate: (date: string) => void;
  onSelectSlot: (slot: BookableSlot) => void;
  onWeekChange: (weekStart: string) => void;
  /** Rappel au-dessus des créneaux du jour choisi (heures d'une promotion, lot 4). */
  dayHeader?: ReactNode;
  /** Remplace « aucun créneau » quand la fenêtre entière est vide (prochain créneau, sorties). */
  emptyContent?: ReactNode;
}

function getVariantClasses(variant: 'tunnel' | 'manage'): {
  datepicker: string;
  datepickerHeader: string;
  datepickerMonth: string;
  datepickerNav: string;
  datepickerGrid: string;
  datepickerDay: string;
  datepickerDayToday: string;
  datepickerDaySelected: string;
  datepickerDayDisabled: string;
  datepickerDayEmpty: string;
  datepickerDayName: string;
  datepickerDayNumber: string;
  datepickerDayDot: string;
  loading: string;
  loadingSkeleton: string;
  loadingSkeletonSlot: string;
  srOnly: string;
  dayLabel: string;
  timeslots: string;
  timeslotGroup: string;
  timeslotGroupLabel: string;
  timeslotGrid: string;
  timeslot: string;
  timeslotSelected: string;
  timeslotWithPlaces: string;
  timeslotFull: string;
  timeslotInsufficient: string;
  timeslotPlaces: string;
  timeslotPromotion: string;
  empty: string;
} {
  if (variant === 'manage') {
    return {
      datepicker: 'manage-booking-page__datepicker',
      datepickerHeader: 'manage-booking-page__datepicker-header',
      datepickerMonth: 'manage-booking-page__datepicker-month',
      datepickerNav: 'manage-booking-page__datepicker-nav',
      datepickerGrid: 'manage-booking-page__datepicker-grid',
      datepickerDay: 'manage-booking-page__datepicker-day',
      datepickerDayToday: 'manage-booking-page__datepicker-day--today',
      datepickerDaySelected: 'manage-booking-page__datepicker-day--selected',
      datepickerDayDisabled: 'manage-booking-page__datepicker-day--disabled',
      datepickerDayEmpty: 'manage-booking-page__datepicker-day--empty',
      datepickerDayName: 'manage-booking-page__datepicker-day-name',
      datepickerDayNumber: 'manage-booking-page__datepicker-day-number',
      datepickerDayDot: 'manage-booking-page__datepicker-day-dot',
      loading: 'manage-booking-page__slot-loading',
      loadingSkeleton: 'manage-booking-page__slot-skeleton',
      loadingSkeletonSlot: 'manage-booking-page__slot-skeleton-item',
      srOnly: 'manage-booking-page__sr-only',
      dayLabel: 'manage-booking-page__selected-day-label',
      timeslots: 'manage-booking-page__timeslots',
      timeslotGroup: 'manage-booking-page__timeslot-group',
      timeslotGroupLabel: 'manage-booking-page__timeslot-group-label',
      timeslotGrid: 'manage-booking-page__timeslot-grid',
      timeslot: 'manage-booking-page__timeslot',
      timeslotSelected: 'manage-booking-page__timeslot--selected',
      timeslotWithPlaces: 'manage-booking-page__timeslot--with-places',
      timeslotFull: 'manage-booking-page__timeslot--full',
      timeslotInsufficient: 'manage-booking-page__timeslot--insufficient',
      timeslotPlaces: 'manage-booking-page__timeslot-places',
      timeslotPromotion: 'manage-booking-page__timeslot-promotion',
      empty: 'manage-booking-page__status',
    };
  }

  return {
    datepicker: 'bt-datepicker',
    datepickerHeader: 'bt-datepicker__header',
    datepickerMonth: 'bt-datepicker__month',
    datepickerNav: 'bt-datepicker__nav',
    datepickerGrid: 'bt-datepicker__grid',
    datepickerDay: 'bt-datepicker__day',
    datepickerDayToday: 'bt-datepicker__day--today',
    datepickerDaySelected: 'bt-datepicker__day--selected',
    datepickerDayDisabled: 'bt-datepicker__day--disabled',
    datepickerDayEmpty: 'bt-datepicker__day--empty',
    datepickerDayName: 'bt-datepicker__day-name',
    datepickerDayNumber: 'bt-datepicker__day-number',
    datepickerDayDot: 'bt-datepicker__day-dot',
    loading: 'bt-slot-skeleton',
    loadingSkeleton: 'bt-slot-skeleton__grid',
    loadingSkeletonSlot: 'bt-slot-skeleton__slot',
    srOnly: 'bt-sr-only',
    dayLabel: 'bt-timeslots__day-label',
    timeslots: 'bt-timeslots',
    timeslotGroup: 'bt-timeslots__group',
    timeslotGroupLabel: 'bt-timeslots__group-label',
    timeslotGrid: 'bt-timeslots__grid',
    timeslot: 'bt-timeslots__slot',
    timeslotSelected: 'bt-timeslots__slot--selected',
    timeslotWithPlaces: 'bt-timeslots__slot--with-places',
    timeslotFull: 'bt-timeslots__slot--full',
    timeslotInsufficient: 'bt-timeslots__slot--insufficient',
    timeslotPlaces: 'bt-timeslots__slot-places',
    timeslotPromotion: 'bt-timeslots__slot-promotion',
    empty: 'bt-timeslots__empty',
  };
}

/* Pastilles du squelette de chargement : deux lignes de la grille la plus large. */
const SKELETON_SLOTS = [0, 1, 2, 3, 4, 5, 6, 7];

function buildSlotGroups(slots: BookableSlot[], labels: SlotWeekPickerLabels): SlotGroup[] {
  const morning: BookableSlot[] = [];
  const afternoon: BookableSlot[] = [];
  const evening: BookableSlot[] = [];

  for (const slot of slots) {
    const hour = getSlotHour(slot);
    if (hour < 12) morning.push(slot);
    else if (hour < 17) afternoon.push(slot);
    else evening.push(slot);
  }

  const groups: SlotGroup[] = [];
  if (morning.length > 0) groups.push({ label: labels.morning, slots: morning });
  if (afternoon.length > 0) groups.push({ label: labels.afternoon, slots: afternoon });
  if (evening.length > 0) groups.push({ label: labels.evening, slots: evening });
  return groups;
}

export function SlotWeekPicker({
  slots,
  selectedSlot,
  selectedDate,
  weekStart,
  labels,
  locale,
  loading = false,
  variant = 'tunnel',
  onSelectDate,
  onSelectSlot,
  onWeekChange,
  dayHeader,
  emptyContent,
}: SlotWeekPickerProps): React.JSX.Element {
  const classes = getVariantClasses(variant);
  const shortDayFormatter = useMemo(
    (): Intl.DateTimeFormat => new Intl.DateTimeFormat(locale, { weekday: 'short' }),
    [locale],
  );
  const dayMonthFormatter = useMemo(
    (): Intl.DateTimeFormat => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }),
    [locale],
  );
  const weekStartDate = useMemo((): Date => parseLocalDate(weekStart), [weekStart]);
  const [todayStr, setTodayStr] = useState((): string => toISODate(new Date()));

  useEffect((): (() => void) => {
    const now = new Date();
    const msUntilMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime() - now.getTime();
    const timer = setTimeout((): void => setTodayStr(toISODate(new Date())), msUntilMidnight + 500);
    return (): void => clearTimeout(timer);
  }, [todayStr]);

  const days = useMemo((): { date: Date; iso: string }[] => {
    const result: { date: Date; iso: string }[] = [];
    for (let index = 0; index < 7; index += 1) {
      const date = addDays(weekStartDate, index);
      result.push({ date, iso: toISODate(date) });
    }
    return result;
  }, [weekStartDate]);

  /* « 16 – 22 octobre », « 29 octobre – 4 novembre » : les jours affichés, pas un mois. */
  const monthLabel = useMemo((): string => {
    const firstDay = days[0]?.date;
    const lastDay = days[6]?.date;
    if (!firstDay || !lastDay) return '';
    const start =
      firstDay.getMonth() === lastDay.getMonth() ? String(firstDay.getDate()) : dayMonthFormatter.format(firstDay);
    return `${start} – ${dayMonthFormatter.format(lastDay)}`;
  }, [days, dayMonthFormatter]);

  /* La fenêtre précédente ne remonte jamais avant aujourd'hui. */
  const canGoPreviousWeek = weekStart > todayStr;

  const availableDates = useMemo((): Set<string> => {
    return new Set(
      slots.map((slot): string | undefined => getSlotDate(slot)).filter((date): date is string => Boolean(date)),
    );
  }, [slots]);

  const selectedDaySlots = useMemo(
    (): BookableSlot[] => (selectedDate ? slots.filter((slot): boolean => getSlotDate(slot) === selectedDate) : []),
    [selectedDate, slots],
  );

  const selectedDayLabel = useMemo((): string | undefined => {
    if (!selectedDate) return undefined;
    return parseLocalDate(selectedDate).toLocaleDateString(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  }, [locale, selectedDate]);

  const slotGroups = useMemo((): SlotGroup[] => buildSlotGroups(selectedDaySlots, labels), [labels, selectedDaySlots]);
  const showSlotGroupLabels = selectedDaySlots.length >= 6;

  const handlePreviousWeek = useCallback((): void => {
    if (!canGoPreviousWeek) return;
    const previous = toISODate(addDays(weekStartDate, -7));
    onWeekChange(previous < todayStr ? todayStr : previous);
  }, [canGoPreviousWeek, onWeekChange, weekStartDate, todayStr]);

  const handleNextWeek = useCallback((): void => {
    onWeekChange(toISODate(addDays(weekStartDate, 7)));
  }, [onWeekChange, weekStartDate]);

  const groupsToRender = showSlotGroupLabels ? slotGroups : [{ label: '', slots: selectedDaySlots }];

  /* ── Bande de jours compacte (scroll-snap) ────
   * En layout compact la grille de jours devient une bande scrollable
   * horizontale (CSS, container btmain) : on recentre le jour sélectionné.
   * `scrollTo` sur le conteneur uniquement — jamais `scrollIntoView`, qui
   * ferait défiler la page hôte. No-op quand la grille 7 jours ne déborde pas. */
  const dayGridRef = useRef<HTMLDivElement | null>(null);
  useEffect((): void => {
    const grid = dayGridRef.current;
    if (!grid || !selectedDate || grid.scrollWidth <= grid.clientWidth) return;
    const selectedButton = grid.querySelector<HTMLButtonElement>('[aria-pressed="true"]');
    if (!selectedButton) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    grid.scrollTo({
      left: selectedButton.offsetLeft - (grid.clientWidth - selectedButton.offsetWidth) / 2,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }, [selectedDate]);

  return (
    <>
      <div className={classes.datepicker}>
        <div className={classes.datepickerHeader}>
          <button
            type="button"
            className={classes.datepickerNav}
            disabled={!canGoPreviousWeek || loading}
            aria-label={labels.previousWeek}
            onClick={handlePreviousWeek}
          >
            ‹
          </button>
          <span className={classes.datepickerMonth}>{monthLabel}</span>
          <button
            type="button"
            className={classes.datepickerNav}
            disabled={loading}
            aria-label={labels.nextWeek}
            onClick={handleNextWeek}
          >
            ›
          </button>
        </div>

        <div className={classes.datepickerGrid} ref={dayGridRef}>
          {days.map(({ date, iso }): React.JSX.Element => {
            const isPast = iso < todayStr;
            const isToday = iso === todayStr;
            const isSelected = iso === selectedDate;
            const hasSlots = availableDates.has(iso);
            const dayClassName = [
              classes.datepickerDay,
              isToday && classes.datepickerDayToday,
              isSelected && classes.datepickerDaySelected,
              isPast && classes.datepickerDayDisabled,
              !hasSlots && !isPast && classes.datepickerDayEmpty,
            ]
              .filter(Boolean)
              .join(' ');
            const dayName = shortDayFormatter.format(date);

            return (
              <button
                key={iso}
                type="button"
                className={dayClassName}
                disabled={isPast || loading}
                aria-label={`${dayName} ${date.getDate()}`}
                aria-pressed={isSelected}
                onClick={(): void => onSelectDate(iso)}
              >
                <span className={classes.datepickerDayName}>{dayName}</span>
                <span className={classes.datepickerDayNumber}>{date.getDate()}</span>
                {hasSlots && !isPast ? <span className={classes.datepickerDayDot} /> : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* Squelette de grille plutôt qu'un spinner seul (bug 35) : la place des créneaux est déjà là. */}
      {loading ? (
        <div className={classes.loading} aria-live="polite" aria-busy="true">
          {labels.loading ? <span className={classes.srOnly}>{labels.loading}</span> : null}
          <div className={classes.loadingSkeleton} aria-hidden="true">
            {SKELETON_SLOTS.map(
              (index): React.JSX.Element => (
                <span key={index} className={classes.loadingSkeletonSlot} />
              ),
            )}
          </div>
        </div>
      ) : null}

      {!loading && selectedDayLabel && selectedDaySlots.length > 0 ? (
        <p className={classes.dayLabel}>{selectedDayLabel}</p>
      ) : null}

      {!loading && selectedDaySlots.length > 0 ? dayHeader : null}

      {!loading && selectedDaySlots.length > 0 ? (
        <div className={classes.timeslots}>
          {groupsToRender.map(
            (group): React.JSX.Element => (
              <div key={group.label || 'slots'} className={classes.timeslotGroup}>
                {group.label ? <span className={classes.timeslotGroupLabel}>{group.label}</span> : null}
                <div className={classes.timeslotGrid}>
                  {group.slots.map((slot): React.JSX.Element => {
                    const isSelected = selectedSlot !== undefined && selectedSlot.slot_start === slot.slot_start;
                    // Compteur de places (séances de groupe) : présent sur tout créneau séance
                    // plafonné, y compris session vide (« Toujours »). Grisage quand la capacité
                    // restante ne couvre pas le groupe demandé — le créneau reste visible.
                    const remaining = getSlotRemainingPlaces(slot);
                    const isFull = remaining === 0;
                    const isInsufficient = !isFull && isSlotBelowRequestedPartySize(slot);
                    const isBlocked = isFull || isInsufficient;
                    const placesLabel =
                      remaining === undefined
                        ? undefined
                        : isFull
                          ? labels.full
                          : remaining === 1
                            ? labels.placesOne
                            : labels.placesOther?.replace('{count}', String(remaining));
                    // Créneau dans les heures d'une promotion (lot 4) : « −15 % » marqué par le serveur.
                    const promotionMark = slot.promotion_label;
                    const ariaLabel = placesLabel
                      ? `${getSlotTimeLabel(slot)} — ${isBlocked && labels.insufficientForParty ? labels.insufficientForParty : placesLabel}`
                      : promotionMark
                        ? `${getSlotTimeLabel(slot)} — ${promotionMark}`
                        : undefined;
                    return (
                      <button
                        key={`${slot.slot_start}-${slot.slot_end}`}
                        type="button"
                        className={[
                          classes.timeslot,
                          isSelected && classes.timeslotSelected,
                          // Deux lignes (heure, puis places ou remise) : même mise en page.
                          (placesLabel !== undefined || promotionMark) && classes.timeslotWithPlaces,
                          isFull && classes.timeslotFull,
                          isInsufficient && classes.timeslotInsufficient,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        disabled={isBlocked}
                        aria-pressed={isBlocked ? undefined : isSelected}
                        aria-label={ariaLabel}
                        onClick={(): void => {
                          if (isBlocked) return;
                          onSelectSlot(slot);
                        }}
                      >
                        {placesLabel !== undefined || promotionMark ? (
                          <>
                            <span>{getSlotTimeLabel(slot)}</span>
                            {promotionMark ? (
                              <span className={classes.timeslotPromotion} aria-hidden="true">
                                {promotionMark}
                              </span>
                            ) : null}
                            {placesLabel !== undefined ? (
                              <span className={classes.timeslotPlaces}>{placesLabel}</span>
                            ) : null}
                          </>
                        ) : (
                          getSlotTimeLabel(slot)
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ),
          )}
        </div>
      ) : null}

      {!loading && selectedDate && selectedDaySlots.length === 0 ? (
        <p className={classes.empty}>{labels.noSlotsForDay ?? labels.noSlots}</p>
      ) : null}

      {!loading && !selectedDate && slots.length === 0
        ? (emptyContent ?? <p className={classes.empty}>{labels.noSlots}</p>)
        : null}
    </>
  );
}
